import axios, { AxiosInstance } from 'axios';
import { API_SUBSCRIPTION_KEY, BASE_API_URLS } from '../config/appConfig';
import { loginRequest, msalInstance } from '../config/msalConfig';
import { getPartnerSession } from './partnerSession';

type BaseUrlKey = keyof typeof BASE_API_URLS;

async function getAccessToken(): Promise<string> {
  // A partner iframe holds an Elora session JWT rather than a B2C token. MSAL
  // has no account for it, so acquireTokenSilent would fail and bounce the
  // frame to the login page — the one thing the hand-over exists to avoid.
  const partnerSession = getPartnerSession();
  if (partnerSession) {
    return partnerSession.accessToken;
  }

  const account = msalInstance.getAllAccounts()[0];
  try {
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account,
    });
    return response.accessToken;
  } catch (err) {
    await msalInstance.loginRedirect(loginRequest);
    throw new Error('Re-authentication required');
  }
}

const createApiClient = (
  baseURL: string,
  timeout = 10000,
  type: BaseUrlKey = 'agent',
  authToken?: string
): AxiosInstance =>
  axios.create({
    baseURL,
    timeout,
    headers: {
      'Content-Type': 'application/json',
      'Ocp-Apim-Subscription-Key': API_SUBSCRIPTION_KEY[type],
      ...(authToken && { Authorization: `Bearer ${authToken}` }),
    },
  });

const getBaseUrl = (baseUrlKey: BaseUrlKey): string => {
  return BASE_API_URLS[baseUrlKey];
};

const apiRequest = async (
  baseUrlKey: BaseUrlKey = 'agent',
  method: string,
  url: string,
  data: any = null,
  params: any = null,
  timeout = 10000
) => {
  try {
    const baseURL = getBaseUrl(baseUrlKey);
    const authToken = await getAccessToken();
    const apiClient = createApiClient(baseURL, timeout, baseUrlKey, authToken);

    const response = await apiClient({
      method,
      url,
      data,
      params,
    });

    return response.data;
  } catch (error: any) {
    if (error.response) {
      const payload = error.response.data?.message;
      const message =
        (typeof payload === 'string' ? payload : payload?.message) ||
        error.response.statusText ||
        'Server error';
      console.error(`Error ${error.response.status}: ${message}`);
      throw new Error(message);
    } else if (error.request) {
      console.error('No response received:', error.request);
      throw new Error('No response from server');
    } else {
      console.error('Error', error.message);
      throw new Error('Request error');
    }
  }
};

export default apiRequest;
